import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  UsersFour, Plus, X, CircleNotch, ArrowLeft, ArrowRight, Folder,
  UserPlus, Trash, SignOut, PencilSimple, ListChecks,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import { NewProjectModal } from "./ProjectsList";

/**
 * Teams module — Command Center P2 add-on.
 *
 * Two views in one file for lean routing:
 *   /app/teams        → list of my teams + create
 *   /app/teams/:tid   → team detail (members + team-scoped projects)
 *
 * The router chooses which one to render based on the `:tid` URL param.
 */
export default function TeamsPage() {
  const { tid } = useParams();
  return tid ? <TeamDetail tid={tid}/> : <TeamsList/>;
}


function TeamsList() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/teams");
      setTeams(Array.isArray(data) ? data : []);
    } catch { setTeams([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createTeam = async (payload) => {
    const { data } = await api.post("/teams", payload);
    setCreateOpen(false);
    navigate(`/app/teams/${data.team_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="teams-list">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
            Command Center · Teams
          </div>
          <h1 className="mt-1 font-display text-[34px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                letterSpacing: "0.02em",
              }}>
            Your Teams
          </h1>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="teams-new"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] transition-transform active:scale-95"
          style={{
            background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
            color: "#0a0703",
            boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
          }}>
          <Plus size={13} weight="bold"/> New Team
        </button>
      </header>

      {loading ? (
        <div className="rounded-2xl p-10 text-center"
             style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
          <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
        </div>
      ) : teams.length === 0 ? (
        <div className="rounded-2xl p-10 text-center" data-testid="teams-empty"
             style={{
               background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
               border: "1px solid rgba(212,175,55,0.45)",
             }}>
          <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
               style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
            <UsersFour size={22} weight="fill" color="#0a0703"/>
          </div>
          <div className="mt-4 font-display text-[24px] text-[#FFD700]">Better together.</div>
          <div className="mt-1 text-[13px] text-[#F2DC8C]/70 max-w-md mx-auto">
            Create a team, invite your friends, and every project you spin up for that team
            is instantly shared with everyone in it.
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="teams-empty-create"
            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em]"
            style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            <Plus size={12} weight="bold"/> New Team
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teams-grid">
          {teams.map((t) => <TeamTile key={t.team_id} team={t}/>)}
        </div>
      )}

      <AnimatePresence>
        {createOpen && <TeamModal onClose={() => setCreateOpen(false)} onSave={createTeam}/>}
      </AnimatePresence>
    </div>
  );
}


function TeamTile({ team }) {
  return (
    <Link
      to={`/app/teams/${team.team_id}`}
      data-testid={`team-tile-${team.team_id}`}
      className="group block rounded-2xl p-5 transition-all hover:-translate-y-0.5"
      style={{
        background:
          "radial-gradient(circle at 100% 0%, rgba(255,215,0,0.08) 0%, transparent 55%)," +
          "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
        border: "1px solid rgba(212,175,55,0.45)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
      }}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-display text-[20px] text-[#FFD700] leading-tight truncate">
            {team.name}
          </div>
          {team.description && (
            <div className="mt-1 text-[12px] text-[#F2DC8C]/70 line-clamp-2">
              {team.description}
            </div>
          )}
        </div>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <UsersFour size={16} weight="fill" color="#0a0703"/>
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 group-hover:text-[#FFD700] transition-colors">
        <span>{team.member_count} member{team.member_count === 1 ? "" : "s"} · {team.project_count} project{team.project_count === 1 ? "" : "s"}</span>
        <ArrowRight size={12}/>
      </div>
    </Link>
  );
}


function TeamDetail({ tid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get(`/teams/${tid}`),
        api.get(`/teams/${tid}/projects`),
      ]);
      setTeam(tRes.data); setProjects(pRes.data || []);
    } catch (ex) {
      if (ex?.response?.status === 404) setNotFound(true);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tid]);

  const isOwner = team && user && team.owner_id === user.user_id;

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the team?")) return;
    await api.delete(`/teams/${tid}/members/${uid}`); load();
  };
  const deleteTeam = async () => {
    if (!window.confirm("Delete this team? Its projects will be detached but not deleted.")) return;
    await api.delete(`/teams/${tid}`);
    navigate("/app/teams");
  };
  const leaveTeam = async () => {
    if (!window.confirm("Leave this team? You'll lose access to its projects.")) return;
    await api.post(`/teams/${tid}/leave`);
    navigate("/app/teams");
  };
  const saveEdit = async (payload) => {
    await api.put(`/teams/${tid}`, payload);
    setEditOpen(false); load();
  };
  const createProject = async (payload) => {
    const { data } = await api.post("/projects", { ...payload, team_id: tid });
    setNewProjectOpen(false);
    navigate(`/app/projects/${data.project_id}`);
  };

  if (loading) return (
    <div className="max-w-6xl mx-auto rounded-2xl p-10 text-center"
         style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
      <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
    </div>
  );
  if (notFound || !team) return (
    <div className="max-w-3xl mx-auto rounded-2xl p-10 text-center" data-testid="team-404"
         style={{ border: "1px solid rgba(255,93,115,0.4)", background: "rgba(0,0,0,0.5)" }}>
      <div className="font-display text-[24px] text-[#FF5D73]">Team not found</div>
      <Link to="/app/teams" className="mt-4 inline-block text-[11px] uppercase tracking-widest text-[#FFD700]">
        Back to Teams
      </Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="team-detail">
      <Link
        to="/app/teams"
        data-testid="team-back"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> All Teams
      </Link>

      {/* Header */}
      <div className="rounded-2xl p-5 flex flex-wrap items-start gap-4"
           style={{
             background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
             border: "1px solid rgba(255,215,0,0.4)",
             boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
           }}>
        <span className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <UsersFour size={22} weight="fill" color="#0a0703"/>
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-[28px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
            {team.name}
          </h1>
          {team.description && (
            <p className="mt-1 text-[13px] text-[#F2DC8C]/75 leading-relaxed">{team.description}</p>
          )}
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">
            {team.members?.length || 1} member{team.members?.length === 1 ? "" : "s"} ·
            {team.project_count} project{team.project_count === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isOwner ? (
            <>
              <button onClick={() => setEditOpen(true)} data-testid="team-edit"
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#F2DC8C] hover:text-[#FFD700]">
                <PencilSimple size={11}/> Edit
              </button>
              <button onClick={deleteTeam} data-testid="team-delete"
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
                <Trash size={11}/> Delete
              </button>
            </>
          ) : (
            <button onClick={leaveTeam} data-testid="team-leave"
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
              <SignOut size={11}/> Leave
            </button>
          )}
        </div>
      </div>

      {/* Members */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]/75">Members</div>
          {isOwner && (
            <button onClick={() => setInviteOpen(true)}
                    data-testid="team-invite"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-xl font-display uppercase text-[10px] tracking-[0.22em]"
                    style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
              <UserPlus size={11}/> Invite Friend
            </button>
          )}
        </div>
        <div className="rounded-2xl divide-y" style={{
          background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.4)",
        }}>
          {team.members?.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 p-3"
                 data-testid={`team-member-${m.user_id}`}
                 style={{ borderColor: "rgba(212,175,55,0.15)" }}>
              <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center shrink-0"
                   style={{ border: "1.5px solid #D4AF37" }}>
                {m.avatar_image ? (
                  <img src={`${API}/files/${m.avatar_image}`} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <span className="text-[13px] font-display text-[#FFD700]">
                    {(m.name || m.username || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <UserLink username={m.username} className="block">
                  <div className="text-[14px] text-[#F2DC8C] truncate">{m.name || "@" + m.username}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 truncate">@{m.username}</div>
                </UserLink>
              </div>
              {m.user_id === team.owner_id ? (
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ border: "1px solid rgba(255,215,0,0.55)", color: "#FFD700" }}>
                  Owner
                </span>
              ) : isOwner && (
                <button onClick={() => removeMember(m.user_id)}
                        data-testid={`team-member-remove-${m.user_id}`}
                        className="text-[10px] uppercase tracking-widest text-[#FF5D73] hover:text-red-400">
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Projects */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#F2DC8C]/75">Team Projects</div>
          <button onClick={() => setNewProjectOpen(true)}
                  data-testid="team-new-project"
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-xl font-display uppercase text-[10px] tracking-[0.22em]"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            <Plus size={11} weight="bold"/> New Project
          </button>
        </div>
        {projects.length === 0 ? (
          <div className="rounded-2xl p-8 text-center text-[13px] text-[#F2DC8C]/60"
               style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.35)" }}>
            No projects yet. Spin one up — everyone on the team gets instant access.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => {
              const pct = p.tasks_total ? Math.round((p.tasks_done / p.tasks_total) * 100) : 0;
              return (
                <Link key={p.project_id} to={`/app/projects/${p.project_id}`}
                      data-testid={`team-project-${p.project_id}`}
                      className="rounded-2xl p-4 block transition-all hover:-translate-y-0.5"
                      style={{
                        background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
                        border: "1px solid rgba(212,175,55,0.4)",
                      }}>
                  <div className="flex items-start justify-between">
                    <div className="font-display text-[18px] text-[#FFD700] truncate">{p.name}</div>
                    <Folder size={16} weight="fill" className="text-[#FFD700]"/>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(212,175,55,0.15)" }}>
                    <div className="h-full" style={{
                      width: `${pct}%`,
                      background: "linear-gradient(90deg, #FFE58A, #FFD700 60%, #D4AF37)",
                    }}/>
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1"><ListChecks size={11}/> {p.tasks_done}/{p.tasks_total}</span>
                    <span>{pct}%</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      <AnimatePresence>
        {inviteOpen && (
          <InviteMemberModal
            tid={tid}
            onClose={() => setInviteOpen(false)}
            onInvited={() => { setInviteOpen(false); load(); }}
          />
        )}
        {editOpen && (
          <TeamModal team={team} onClose={() => setEditOpen(false)} onSave={saveEdit}/>
        )}
        {newProjectOpen && (
          <NewProjectModal defaultTeamId={tid} onClose={() => setNewProjectOpen(false)} onCreate={createProject}/>
        )}
      </AnimatePresence>
    </div>
  );
}


function TeamModal({ team, onClose, onSave }) {
  const [name, setName] = useState(team?.name || "");
  const [description, setDescription] = useState(team?.description || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { await onSave({ name: name.trim(), description: description.trim() || null }); }
    finally { setBusy(false); }
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[110] flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
                data-testid="team-modal">
      <motion.form initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                   onClick={(e) => e.stopPropagation()}
                   onSubmit={submit}
                   className="w-full max-w-lg rounded-2xl overflow-hidden"
                   style={{
                     background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                     border: "1.5px solid rgba(255,215,0,0.55)",
                     boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
                   }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <UsersFour size={16} weight="fill" className="text-[#FFD700]"/>
          <div className="font-display text-[16px] text-[#FFD700]">{team ? "Edit Team" : "New Team"}</div>
          <button type="button" onClick={onClose}
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block rounded-xl p-3"
                 style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">Name</div>
            <input autoFocus data-testid="team-name" value={name}
                   onChange={(e) => setName(e.target.value.slice(0, 140))}
                   placeholder="Aloha Studio Crew"
                   className="w-full bg-transparent text-[15px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
          </label>
          <label className="block rounded-xl p-3"
                 style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">Description (optional)</div>
            <textarea data-testid="team-desc" value={description}
                      onChange={(e) => setDescription(e.target.value.slice(0, 4000))} rows={3}
                      placeholder="Who is this crew? What are you building together?"
                      className="w-full bg-transparent text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none resize-none"/>
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t"
             style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <button type="button" onClick={onClose}
                  className="px-4 h-10 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
                  style={{ border: "1px solid rgba(212,175,55,0.45)" }}>Cancel</button>
          <button type="submit" disabled={busy || !name.trim()} data-testid="team-save"
                  className="px-4 h-10 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            {busy ? "Saving…" : (team ? "Save" : "Create Team")}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}


function InviteMemberModal({ tid, onClose, onInvited }) {
  const [friends, setFriends] = useState([]);
  const [existingIds, setExistingIds] = useState(new Set());
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [fRes, tRes] = await Promise.all([
          api.get("/friends"),
          api.get(`/teams/${tid}`),
        ]);
        setFriends(fRes.data || []);
        setExistingIds(new Set((tRes.data?.members || []).map((m) => m.user_id)));
      } catch {}
    })();
  }, [tid]);

  const invite = async (friend) => {
    setErr(""); setBusy(friend.user_id);
    try {
      await api.post(`/teams/${tid}/members`, { username: friend.username });
      onInvited?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not invite that friend.");
    } finally { setBusy(""); }
  };

  const invitable = friends.filter((f) => !existingIds.has(f.user_id));

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[110] flex items-center justify-center p-4"
                style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
                data-testid="team-invite-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-md rounded-2xl overflow-hidden"
                  style={{
                    background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
                    border: "1.5px solid rgba(255,215,0,0.55)",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.75)",
                  }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <UserPlus size={16} weight="fill" className="text-[#FFD700]"/>
          <div className="font-display text-[16px] text-[#FFD700]">Invite a Friend</div>
          <button type="button" onClick={onClose}
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-4">
          {err && <div className="mb-3 text-[12px] text-[#FF5D73]" data-testid="team-invite-error">{err}</div>}
          {friends.length === 0 ? (
            <div className="p-6 text-center text-[13px] text-[#F2DC8C]/70" data-testid="team-invite-empty">
              You haven&apos;t added any friends yet. Head to{" "}
              <Link to="/app/messages" onClick={onClose} className="text-[#FFD700] underline">
                Messages
              </Link>{" "}
              to add friends first.
            </div>
          ) : invitable.length === 0 ? (
            <div className="p-6 text-center text-[13px] text-[#F2DC8C]/70">
              All your friends are already on this team. 💫
            </div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto space-y-1">
              {invitable.map((f) => (
                <div key={f.user_id} data-testid={`invite-friend-${f.username}`}
                     className="flex items-center gap-3 p-2 rounded-lg"
                     style={{ border: "1px solid rgba(212,175,55,0.3)" }}>
                  <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center shrink-0"
                       style={{ border: "1.5px solid #D4AF37" }}>
                    {f.avatar_image ? (
                      <img src={`${API}/files/${f.avatar_image}`} alt="" className="w-full h-full object-cover"/>
                    ) : (
                      <span className="text-[12px] font-display text-[#FFD700]">
                        {(f.name || f.username || "?").slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <UserLink username={f.username} className="block">
                      <div className="text-[13px] text-[#F2DC8C] truncate">{f.name || "@" + f.username}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">@{f.username}</div>
                    </UserLink>
                  </div>
                  <button onClick={() => invite(f)}
                          disabled={busy === f.user_id}
                          data-testid={`invite-add-${f.username}`}
                          className="h-8 px-3 rounded-lg font-display uppercase text-[10px] tracking-[0.22em] disabled:opacity-50"
                          style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
                    {busy === f.user_id ? "Adding…" : "Invite"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
